import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';

/** Width of the legacy editor panels (`mtm-side-panel` mixin in src/theme/_side-panel.scss). */
export const SIDE_PANEL_WIDTH = 420;

/** Below this width a right side panel covers the page (with a scrim) instead of pushing it. */
export const SIDE_PANEL_OVERLAY_QUERY = '(max-width: 1023px)';

/** UI state shared by the shell pieces (sidebar, topbar) and the pages inside it. */
@Injectable({ providedIn: 'root' })
export class ShellStateService {
  private readonly media =
    inject(DOCUMENT).defaultView?.matchMedia?.(SIDE_PANEL_OVERLAY_QUERY) ?? null;

  /** Below 768 px the sidebar is a drawer opened from the topbar menu button. */
  readonly mobileNavOpen = signal(false);

  /** Trailing breadcrumb label set by a detail page (e.g. a group name); null otherwise. */
  readonly breadcrumbDetail = signal<string | null>(null);

  /** True on narrow viewports, where side panels overlay the page instead of pushing it. */
  readonly sidePanelOverlay = signal(this.media?.matches ?? false);

  private readonly openPanels = signal<readonly number[]>([]);

  /** Width of the widest open right side panel; the main column shrinks by this much. */
  readonly sidePanelWidth = computed(() => Math.max(0, ...this.openPanels()));

  constructor() {
    const onChange = (e: MediaQueryListEvent) => this.sidePanelOverlay.set(e.matches);
    this.media?.addEventListener?.('change', onChange);
    inject(DestroyRef).onDestroy(() => this.media?.removeEventListener?.('change', onChange));
  }

  /** Registers an open side panel of the given width; call the returned function on close. */
  openSidePanel(width: number): () => void {
    this.openPanels.update((p) => [...p, width]);
    return () =>
      this.openPanels.update((p) => {
        const i = p.indexOf(width);
        return i < 0 ? p : [...p.slice(0, i), ...p.slice(i + 1)];
      });
  }
}

/**
 * Makes the calling component (a right side panel) push the shell content aside for as long as
 * it lives. Must run in an injection context (field initializer or constructor).
 */
export function pushShellContent(width: number): void {
  const release = inject(ShellStateService).openSidePanel(width);
  inject(DestroyRef).onDestroy(release);
}
